import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import StampBadge from "./StampBadge";
import HandArrow from "./HandArrow";
import { track } from "@/lib/analytics";

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" } }),
};

export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-[#FFC20E] grain pt-28 sm:pt-32 lg:pt-36 pb-10 lg:pb-16">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-6 items-center">
          {/* LEFT */}
          <div className="relative">
            <motion.h1
              variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="font-display font-black uppercase text-[#0C0C0C] display-tight text-6xl sm:text-8xl lg:text-[8.5rem]"
              data-testid="hero-headline"
            >
              TI È<br />PRESO<br />UNGRIP?
            </motion.h1>

            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="mt-6 text-[#0C0C0C] font-semibold text-lg sm:text-xl leading-snug max-w-md"
            >
              Focaccia croccante.<br />
              Pulled pork fatto da noi.<br />
              Il resto lo capisci<br />
              al primo morso.
            </motion.p>

            <motion.div
              variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={() => { track("hero_cta_click", { cta: "scopri_menu" }); scrollTo("menu"); }}
                className="group inline-flex items-center gap-2 rounded-full bg-[#0C0C0C] text-[#FFC20E] font-display font-extrabold uppercase tracking-wide text-base sm:text-lg px-7 py-3.5 hover:scale-[1.03] transition-transform duration-200"
                data-testid="hero-discover-menu-cta"
              >
                SCOPRI IL MENU
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <button
                onClick={() => { track("club_cta_click", { cta: "hero" }); scrollTo("club"); }}
                className="inline-flex items-center rounded-full border-2 border-[#0C0C0C] text-[#0C0C0C] font-display font-extrabold uppercase tracking-wide text-base sm:text-lg px-7 py-3.5 hover:bg-[#0C0C0C] hover:text-[#FFC20E] transition-colors duration-200"
                data-testid="hero-join-club-cta"
              >
                ENTRA NEL CLUB
              </button>
            </motion.div>

            <div className="mt-6 flex items-center gap-1 text-[#0C0C0C]">
              <HandArrow className="w-12 h-8 -scale-x-100 rotate-[170deg]" />
              <span className="font-hand text-2xl font-bold" data-testid="hero-handwritten-note">
                potresti sporcarti le mani
              </span>
            </div>
          </div>

          {/* RIGHT */}
          <div className="relative">
            {/* vertical sensory text */}
            <div className="absolute -top-2 right-0 sm:right-2 z-20 text-right hidden sm:block">
              <span className="font-hand text-2xl lg:text-3xl font-bold text-[#0C0C0C] leading-tight block -rotate-3">
                CROCCANTE<br />FUORI.<br />SUCCOSO<br />DENTRO.
              </span>
              <HandArrow className="w-16 h-10 ml-auto mt-1 rotate-[100deg]" />
            </div>

            <div className="relative grid grid-cols-2 gap-2 sm:gap-4 items-end pt-16 sm:pt-8">
              <motion.figure
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                className="text-center"
              >
                <img
                  src="/assets/01_honey.jpg"
                  alt="Focaccia HONEY — pulled pork, cheese, cavolo viola, honey mustard"
                  className="w-full rounded-2xl object-cover shadow-2xl aspect-square"
                  data-testid="hero-product-image-1"
                  loading="eager"
                />
                <figcaption className="font-display font-black uppercase text-[#0C0C0C] text-xl sm:text-2xl mt-2">HONEY</figcaption>
              </motion.figure>
              <motion.figure
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.34, ease: "easeOut" }}
                className="text-center"
              >
                <img
                  src="/assets/05_spicy.jpg"
                  alt="Focaccia SPICY — pulled pork, cheese, jalapeño, salsa UNGRIP"
                  className="w-full rounded-2xl object-cover shadow-2xl aspect-square"
                  data-testid="hero-product-image-2"
                  loading="eager"
                />
                <figcaption className="font-display font-black uppercase text-[#0C0C0C] text-xl sm:text-2xl mt-2">SPICY</figcaption>
              </motion.figure>
            </div>

            <div className="absolute -bottom-4 right-2 sm:right-4 w-24 sm:w-32 lg:w-36 z-20">
              <StampBadge />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
